import { getApiBaseUrl } from '../../../utils/apiConfig';

function authHeaders() {
  const token = localStorage.getItem('authToken');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

/**
 * @param {string} [period] 7d|30d|90d|all
 */
export async function fetchReembolsosOverview(period = '30d') {
  const base = getApiBaseUrl();
  const q = new URLSearchParams({ period: String(period || '30d') });
  const res = await fetch(`${base}/api/financeiro/reembolsos?${q}`, {
    headers: authHeaders(),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json.success) {
    throw new Error(json.message || 'Erro ao carregar reembolsos');
  }
  return json.data;
}

/** Categorias (LCMY_GO_EXPENSE_TYPE) */
export async function fetchExpenseTypes(q = '') {
  const base = getApiBaseUrl();
  const params = new URLSearchParams();
  if (q) params.set('q', String(q));
  const res = await fetch(`${base}/api/financeiro/reembolsos/expense-types?${params}`, {
    headers: authHeaders(),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json.success) {
    throw new Error(json.message || 'Erro ao carregar categorias');
  }
  return json.data;
}

export async function createExpenseType(payload) {
  const base = getApiBaseUrl();
  const res = await fetch(`${base}/api/financeiro/reembolsos/expense-types`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload || {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json.success) {
    throw new Error(json.message || 'Erro ao criar categoria');
  }
  return json.data;
}

export async function updateExpenseType(id, payload) {
  const base = getApiBaseUrl();
  const res = await fetch(
    `${base}/api/financeiro/reembolsos/expense-types/${encodeURIComponent(String(id))}`,
    {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify(payload || {}),
    }
  );
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json.success) {
    throw new Error(json.message || 'Erro ao atualizar categoria');
  }
  return json.data;
}

/**
 * @param {{ ids: Array<string|number>, action: 'approve'|'pay'|'reject_definitive'|'reject_review', reason?: string }} payload
 */
export async function updateExpenseStatus(payload) {
  const base = getApiBaseUrl();
  const res = await fetch(`${base}/api/financeiro/reembolsos/expenses/status`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      ids: Array.isArray(payload?.ids) ? payload.ids : [],
      action: payload?.action,
      reason: payload?.reason || undefined,
    }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json.success) {
    throw new Error(json.message || 'Erro ao atualizar status da despesa');
  }
  return json.data;
}

/** Ocorrências (LCMY_GO_EXPENSE_TIMELINE) de uma despesa — mais recente primeiro */
export async function fetchExpenseTimeline(expenseId) {
  const base = getApiBaseUrl();
  const res = await fetch(
    `${base}/api/financeiro/reembolsos/expenses/${encodeURIComponent(String(expenseId))}/timeline`,
    { headers: authHeaders() }
  );
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json.success) {
    throw new Error(json.message || 'Erro ao carregar ocorrências');
  }
  return json.data;
}

/**
 * Técnicos vinculados a área + área com coordenador (mesma regra Equipe 2).
 */
export async function fetchExpenseLinkedTechnicians() {
  const base = getApiBaseUrl();
  const headers = authHeaders();
  const [techRes, areasRes, areaTeamRes, areaCoordRes] = await Promise.all([
    fetch(`${base}/api/team/technicians`, { headers }),
    fetch(`${base}/api/team/areas`, { headers }),
    fetch(`${base}/api/team/areateam`, { headers }),
    fetch(`${base}/api/team/areacoord`, { headers }),
  ]);
  const [techJson, areasJson, areaTeamJson, areaCoordJson] = await Promise.all([
    techRes.json().catch(() => ({})),
    areasRes.json().catch(() => ({})),
    areaTeamRes.json().catch(() => ({})),
    areaCoordRes.json().catch(() => ({})),
  ]);

  if (!techJson.success) {
    throw new Error(techJson.message || 'Erro ao carregar técnicos');
  }

  const areaTeam = areaTeamJson.success ? areaTeamJson.data || [] : [];
  const areaCoord = areaCoordJson.success ? areaCoordJson.data || [] : [];
  const areasRaw = areasJson.success ? areasJson.data || [] : [];

  const areaHasCoord = new Set(
    (areaCoord || [])
      .filter((v) => v?.id_area != null && v?.id_coordinator != null)
      .map((v) => String(v.id_area))
  );
  const techToArea = new Map();
  for (const v of areaTeam || []) {
    if (v?.id_tech == null || v?.id_area == null) continue;
    techToArea.set(String(v.id_tech).trim(), String(v.id_area));
  }

  const areaNameById = new Map();
  for (const a of areasRaw) {
    if (a?.id == null) continue;
    areaNameById.set(String(a.id), a.nome || a.name || '');
  }

  const list = [];
  for (const t of techJson.data || []) {
    const id = String(t.id ?? '').trim();
    if (!id) continue;
    const situacao = String(t.situacao ?? '')
      .trim()
      .toUpperCase();
    if (situacao && situacao !== 'A') continue;
    const areaId = techToArea.get(id);
    if (!areaId || !areaHasCoord.has(areaId)) continue;
    list.push({
      codtech: id,
      name: t.nome || `Técnico ${id}`,
      areaId,
      areaName: areaNameById.get(areaId) || '',
    });
  }

  list.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
  return list;
}

export async function presignExpenseReceipt(payload) {
  const base = getApiBaseUrl();
  const res = await fetch(`${base}/api/financeiro/reembolsos/receipts/presign`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload || {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json.success) {
    throw new Error(json.message || 'Erro ao preparar upload');
  }
  return json.data;
}

export async function completeExpenseReceipt(payload) {
  const base = getApiBaseUrl();
  const res = await fetch(`${base}/api/financeiro/reembolsos/receipts/complete`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload || {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json.success) {
    throw new Error(json.message || 'Erro ao confirmar upload');
  }
  return json.data;
}

/** PUT no uploadUrl (presign). Não loga a URL. */
export async function putExpenseReceiptFile(uploadUrl, file, contentType) {
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': contentType || file.type || 'application/octet-stream',
    },
    body: file,
  });
  if (!res.ok) {
    throw new Error('Falha no envio do arquivo.');
  }
}

/**
 * Lançamento avulso (painel) → POST LCMY_GO_EXPENSE
 * @param {{
 *   codtech: string,
 *   expenseDate: string,
 *   amount: number,
 *   expenseTypeId: string|number,
 *   paymentType: string,
 *   description?: string,
 *   receiptStorageKeys?: string[],
 * }} payload
 */
export async function createExpense(payload) {
  const base = getApiBaseUrl();
  const res = await fetch(`${base}/api/financeiro/reembolsos/expenses`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      codtech: payload?.codtech,
      expenseDate: payload?.expenseDate,
      amount: payload?.amount,
      expenseTypeId: payload?.expenseTypeId,
      paymentType: payload?.paymentType,
      description: payload?.description || '',
      receiptStorageKeys: Array.isArray(payload?.receiptStorageKeys)
        ? payload.receiptStorageKeys
        : [],
    }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json.success) {
    throw new Error(json.message || 'Erro ao lançar despesa');
  }
  return json.data;
}
